import { useEffect, useState } from "react";
import { AeonyxIcon, AeonyxWordmark } from "./Logo";
import { Button } from "@/components/ui/button";
import { scrollToConfigure, scrollToId } from "@/lib/useScrollReveal";
import { MagnifyingGlass, ShoppingBag, User, SignOut, List, X, CaretDown, SignIn, Snowflake } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NAV = [
  { label: "Products", href: "/#products", hasCaret: true, anchor: "products", offset: 0 },
  { label: "Blogs", href: "/#blogs", anchor: "blogs", offset: 120 },
  { label: "FAQ", href: "/#faq", anchor: "faq", offset: 120 },
  { label: "Contact", href: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count: cartCount, openCart } = useCart();
  const { isAuthenticated, isAdmin, user, openAuth, signOut } = useAuth();
  const navigate = useNavigate();

  const handleUserClick = () => {
    if (!isAuthenticated) {
      openAuth("signin");
      return;
    }
    navigate(isAdmin ? "/admin/dashboard" : "/account");
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out");
    } catch (err) {
      toast.error(err?.message || "Sign out failed");
    }
  };

  const handleAnchorClick = (e, item) => {
    if (!item.anchor) return; // let /contact and other page routes navigate normally
    // If we're not on the home page, let the browser navigate to /#anchor,
    // then scroll after mount (handled by hash-scroll effect below).
    if (window.location.pathname !== "/") return;
    e.preventDefault();
    scrollToId(item.anchor, item.offset ?? 120);
    // Update the URL hash for shareability without jumping.
    if (window.history.replaceState) {
      window.history.replaceState(null, "", `#${item.anchor}`);
    }
  };

  // When the page loads with a hash (e.g. arriving from /contact → /#faq),
  // scroll with header-aware offset after the DOM is settled.
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.replace("#", "");
    const match = NAV.find((n) => n.anchor === id);
    const offset = match ? match.offset ?? 120 : 120;
    // slight delay so lazy sections have measured layout
    const t = setTimeout(() => scrollToId(id, offset), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-[#8A97A8]/25 shadow-[0_2px_10px_-4px_rgba(2,47,107,0.08)]"
          : "bg-white border-b border-transparent"
      }`}
    >
      {/* Navbar: 88px height, generous whitespace */}
      <div
        className="max-w-[1400px] mx-auto flex items-center justify-between gap-8 h-[72px] md:h-[80px] lg:h-[88px]"
        style={{ paddingLeft: "clamp(16px, 2.5vw, 32px)", paddingRight: "clamp(20px, 3vw, 40px)" }}
      >
        {/* Logo — 38 mobile / 46 tablet / 52 laptop / 56 desktop / 60 large */}
        <Link
          to="/"
          data-testid="header-logo-link"
          aria-label="Aeonyx Life Sciences — Home"
          className="flex items-center gap-1 shrink-0 transition-transform duration-[250ms] ease-in-out hover:scale-[1.03] hover:brightness-105 cursor-pointer"
        >
          <AeonyxIcon
            priority
            className="h-[48px] md:h-[58px] lg:h-[66px] xl:h-[72px] 2xl:h-[76px] w-auto select-none"
          />
          <AeonyxWordmark
            priority
            className="h-[30px] md:h-[36px] lg:h-[42px] xl:h-[46px] 2xl:h-[50px] w-auto select-none"
          />
        </Link>

        {/* Nav — 48px after logo, 32px between items */}
        <nav
          className="hidden lg:flex items-center ml-auto"
          style={{ marginRight: "24px", gap: "32px" }}
          aria-label="Primary"
        >
          {NAV.map((item) => {
            if (item.label === "Products") {
              return (
                <div key={item.label} className="relative group">
                  <a
                    href={item.href}
                    onClick={(e) => e.preventDefault()}
                    data-testid="nav-products"
                    aria-haspopup="true"
                    className="inline-flex items-center gap-1 text-[14px] text-navy-deep hover:text-cyan-brand transition-colors whitespace-nowrap cursor-default"
                  >
                    {item.label}
                    <CaretDown size={11} weight="bold" className="opacity-70 group-hover:rotate-180 transition-transform duration-300" />
                  </a>
                  {/* Invisible hover bridge so the dropdown doesn't close moving between trigger and menu */}
                  <div className="absolute left-0 right-0 top-full h-3" aria-hidden="true" />
                  <div
                    role="menu"
                    className="absolute left-1/2 top-[calc(100%+10px)] -translate-x-1/2 w-[320px] rounded-2xl border border-[#8A97A8]/20 bg-white shadow-[0_30px_60px_-25px_rgba(2,47,107,0.35)] p-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 focus-within:opacity-100 focus-within:visible focus-within:translate-y-0 transition-all duration-200 z-50"
                  >
                    <a
                      href="/#products"
                      onClick={(e) => handleAnchorClick(e, { anchor: "products", offset: 0 })}
                      data-testid="nav-products-cryovault"
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-cool-white transition-colors group/item"
                    >
                      <div className="h-10 w-10 shrink-0 rounded-lg bg-cyan-brand/10 grid place-items-center text-cyan-brand group-hover/item:bg-cyan-brand group-hover/item:text-white transition-colors">
                        <Snowflake size={18} weight="duotone" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-semibold text-navy-deep">
                          CryoVault Peptide Fridge
                        </div>
                        <div className="text-[11.5px] text-cool-grey mt-0.5">
                          Bench-top 2–8°C storage
                        </div>
                      </div>
                    </a>
                  </div>
                </div>
              );
            }
            return (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => handleAnchorClick(e, item)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-1 text-[14px] text-navy-deep hover:text-cyan-brand transition-colors whitespace-nowrap"
              >
                {item.label}
                {item.hasCaret && <CaretDown size={11} weight="bold" className="opacity-70" />}
              </a>
            );
          })}
        </nav>

        {/* Right cluster: search / cart / profile / logout with 24px gap */}
        <div className="flex items-center shrink-0" style={{ gap: "12px" }}>
          <div className="hidden sm:flex items-center" style={{ gap: "24px" }}>
            <button
              data-testid="header-search"
              className="text-navy-deep hover:text-cyan-brand transition-colors"
              aria-label="Search"
            >
              <MagnifyingGlass size={20} weight="regular" />
            </button>
            <button
              data-testid="header-cart"
              onClick={openCart}
              className="relative text-navy-deep hover:text-cyan-brand transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag size={20} weight="regular" />
              {cartCount > 0 && (
                <span
                  data-testid="header-cart-count"
                  className="absolute -top-2 -right-2 h-4 min-w-4 px-1 rounded-full bg-teal-brand text-[10px] font-semibold text-navy-deep grid place-items-center"
                >
                  {cartCount}
                </span>
              )}
            </button>
            <button
              data-testid="header-user"
              onClick={handleUserClick}
              className="text-navy-deep hover:text-cyan-brand transition-colors relative"
              aria-label={isAuthenticated ? (isAdmin ? "Open admin dashboard" : "Open account") : "Sign in"}
              title={
                isAuthenticated
                  ? isAdmin
                    ? `Admin — ${user?.email}`
                    : `Signed in as ${user?.email}`
                  : "Sign in"
              }
            >
              {isAuthenticated ? <User size={20} weight="fill" /> : <SignIn size={20} weight="regular" />}
              {isAuthenticated && (
                <span
                  className={`absolute -bottom-1 -right-1 h-2 w-2 rounded-full ring-2 ring-white ${
                    isAdmin ? "bg-amber-400" : "bg-teal-brand"
                  }`}
                />
              )}
            </button>
            {isAuthenticated && (
              <button
                data-testid="header-logout"
                onClick={handleSignOut}
                className="text-navy-deep hover:text-cyan-brand transition-colors"
                aria-label="Sign out"
              >
                <SignOut size={20} weight="regular" />
              </button>
            )}
          </div>

          <Button
            data-testid="header-cta"
            onClick={scrollToConfigure}
            className="hidden xl:inline-flex group relative overflow-hidden ml-3 h-11 px-5 rounded-full bg-cta-gradient text-white text-[13px] font-semibold border border-white/25 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.5),inset_0_-1px_0_rgba(2,47,107,0.35),0_8px_22px_-8px_rgba(1,207,198,0.55)] hover:brightness-110 hover:-translate-y-0.5 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.7),inset_0_-1px_0_rgba(2,47,107,0.35),0_12px_30px_-10px_rgba(2,47,107,0.45),0_0_22px_-6px_rgba(1,207,198,0.6)] transition-all duration-300"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-2 top-0 h-1/2 rounded-full bg-gradient-to-b from-white/45 to-transparent opacity-90"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -inset-y-1 -left-1/3 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/55 to-transparent opacity-0 group-hover:opacity-100 group-hover:left-[110%] transition-all duration-700 ease-out"
            />
            <span className="relative z-10">Protect Peptides</span>
          </Button>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden h-10 w-10 grid place-items-center rounded-full hover:bg-cool-white text-navy-deep transition-colors"
            aria-label="Menu"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-[#8A97A8]/20 bg-white">
          <div className="max-w-[1400px] mx-auto px-5 py-4 flex flex-col">
            {NAV.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => {
                  setMobileOpen(false);
                  handleAnchorClick(e, item);
                }}
                className="py-3 text-navy-deep hover:text-cyan-brand text-[15px] border-b border-[#8A97A8]/15 last:border-b-0"
              >
                {item.label}
              </a>
            ))}
            <Button
              onClick={() => {
                setMobileOpen(false);
                scrollToConfigure();
              }}
              className="mt-4 w-full h-11 rounded-full bg-cta-gradient hover:brightness-110 text-white font-semibold"
            >
              Protect Peptides
            </Button>
            {isAuthenticated ? (
              <Button
                onClick={() => { setMobileOpen(false); navigate("/account"); }}
                variant="outline"
                data-testid="mobile-account"
                className="mt-2 w-full h-11 rounded-full border-[#8A97A8]/30"
              >
                <User size={16} weight="bold" className="mr-1.5" /> My account
              </Button>
            ) : (
              <Button
                onClick={() => { setMobileOpen(false); openAuth("signin"); }}
                variant="outline"
                data-testid="mobile-signin"
                className="mt-2 w-full h-11 rounded-full border-[#8A97A8]/30"
              >
                <SignIn size={16} weight="bold" className="mr-1.5" /> Sign in
              </Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
